import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import { memo, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import styled, { css, keyframes } from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { PairIcon, PairTicker } from '@root/entities/pair/ui/common'
import { PairRank } from '@root/entities/pair/ui/pair-rank'
import { Trend } from '@root/entities/trend'
import { CurrencyIconPopover } from '@root/features/currency/ui/currency-icon-popover/currency-icon-popover'
import { TradeRoute } from '@root/features/routing'
import {
  AmountFormatted,
  MarkerCapShortFormatted,
} from '@root/shared/core/format'

import type { PairModel } from '@root/entities/pair'
import type BigNumber from 'bignumber.js'

type TopbarPairItemProps = {
  pair: PairModel
  amountUsd: BigNumber
  amountFormat: 'mcap' | 'amount'
  percents: number
  withRank?: boolean
  withPreview?: boolean
  actionsSlot?: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const ICON_SIZE = 22
const SHORT_TICKER_MAX_LENGTH = 8

export const TopbarPairItem = memo(
  ({
    className,
    pair,
    amountUsd,
    amountFormat,
    withRank = false,
    withPreview = false,
    percents,
    actionsSlot,
    ...divProps
  }: TopbarPairItemProps) => {
    const trend = Trend.of(percents)
    const [rank] = useAtom(pair.rank)
    const [isHovered, setIsHovered] = useState(false)

    const rootRef = useRef<HTMLAnchorElement>(null)

    useEffect(() => {
      const el = rootRef.current
      if (!el) {
        return
      }
      // TODO: Perf!
      const width = Math.ceil(el.getBoundingClientRect().width)
      el.style.width = `${width}px`
    }, [])

    const hasActions = actionsSlot != undefined
    const isShortTicker = pair.quote.ticker.length < SHORT_TICKER_MAX_LENGTH

    const el = (
      <Container
        className={className}
        {...divProps}
        onPointerEnter={(e) => {
          if (hasActions) {
            setIsHovered(true)
          }
          divProps.onPointerEnter?.(e)
        }}
        onPointerLeave={(e) => {
          if (hasActions) {
            setIsHovered(false)
          }
          divProps.onPointerLeave?.(e)
        }}
      >
        <Root
          ref={rootRef}
          to={TradeRoute.build({
            pairAddress: pair.address,
            network: pair.network,
          })}
        >
          {withRank && rank ? <PairRank size="xs" rank={rank} /> : undefined}

          <PairIcon
            pair={pair}
            size={ICON_SIZE}
            providerSize={8}
            withPreview={false}
            withGoogleLens={false}
            outlined
          />

          <ContentFade $noFade={isShortTicker}>
            <Symbol
              pair={pair}
              asLink={false}
              withFade={!isShortTicker}
              $shortTicker={isShortTicker}
            />

            <Price>
              <AnimatedValue key={amountUsd.toFixed()}>
                {amountFormat === 'mcap' ? (
                  <MarkerCapShortFormatted mcap={amountUsd} />
                ) : (
                  <AmountFormatted
                    amount={amountUsd}
                    currency="$"
                    roundingMode="trunc"
                  />
                )}
              </AnimatedValue>
            </Price>

            <PnlChange $trend={trend}>
              <AnimatedValue key={percents}>
                <Numeric value={percents} preset="percents" forceSign />%
              </AnimatedValue>
            </PnlChange>
          </ContentFade>
        </Root>

        <AnimatePresence initial={false}>
          {hasActions && isHovered ? (
            <motion.div
              layout
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 'auto', opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ ease: 'easeInOut' }}
              style={actionsSlotStyle}
            >
              {actionsSlot}
            </motion.div>
          ) : undefined}
        </AnimatePresence>
      </Container>
    )

    return withPreview ? (
      <CurrencyIconPopover premiumInfo={pair.premiumInfo} currency={pair.quote}>
        {el}
      </CurrencyIconPopover>
    ) : (
      el
    )
  },
)

const actionsSlotStyle = {
  overflow: 'hidden',
}

const Container = styled.div`
  display: flex;
  align-items: center;
`

const Root = styled(Link)`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;
  padding-block: 2px;

  text-decoration: none;
  justify-content: flex-start;

  overflow: hidden;
`

const Symbol = styled(PairTicker)<{ $shortTicker?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;

  ${({ $shortTicker }) =>
    $shortTicker &&
    css`
      min-width: max-content;
    `}
`

const Price = styled.div`
  color: var(--text-12);
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  text-align: start;
`

const PnlChange = styled(Price)<{ $trend: Trend.Trend }>`
  color: ${({ $trend }) =>
    Trend.match($trend, {
      Up: () => 'var(--up-9)',
      Down: () => 'var(--down-9)',
      Neutral: () => 'var(--text-11)',
    })};
`

const valueFlash = keyframes`
  0% { opacity: 0.4; }
  100% { opacity: 1; }
`

const AnimatedValue = styled.span`
  animation: ${valueFlash} 350ms ease-out;
`

const ContentFade = styled.div<{ $noFade?: boolean }>`
  padding-inline: 1px 0;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;

  // for macos
  margin-top: 1px;

  // for windows
  html[data-platform='windows'] & {
    margin-top: 2px;
  }

  --mask-inline-start: 0px;
  --mask-inline-end: ${({ $noFade }) => ($noFade ? '0px' : '8px')};
  --horizontal-mask: linear-gradient(
    90deg,
    transparent,
    black var(--mask-inline-start),
    black calc(100% - var(--mask-inline-end)),
    transparent
  );
  --mask: var(--horizontal-mask);

  mask-image: ${({ $noFade }) => ($noFade ? 'none' : 'var(--mask)')};
  mask-size: 100%;
  mask-position: 0 0;
  mask-repeat: no-repeat;
  padding-inline-end: 2px;
`
